import { CreateTransactionResponse } from "@ac-expo/api-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { handleApiCall } from "../api-error-handler";
import { privateAxios } from "../axiosInstances";
import { CreateTransactionWithoutBusinessNameForm } from "../schemas";
import { AddTicket } from "./basket.mutations";
import { eventsQueryKeys, ticketingQueryKeys } from "../query-keys";

interface NewTicketingOrderBody {
  email: string;
  address: CreateTransactionWithoutBusinessNameForm["address"];
  tickets: AddTicket[];
  badges: AddTicket[];
  discountCodes?: string[];
}

interface UpsertExternalTicketingBody {
  htmlContent: string;
}

interface UpsertExternalTicketingResponse {
  success: boolean;
  ticketing: {
    id: string;
    eventId: string;
    htmlContent: string;
  };
}

interface DeleteExternalTicketingResponse {
  success: boolean;
  message: string;
  ticketing: {
    id: string;
    eventId: string;
    htmlContent: string | null;
  };
}

interface NewTicketingOrderResponse {
  transaction: CreateTransactionResponse;
  // Guest session bearer token, absent for registered accounts
  accessToken?: string;
  // Order-scoped token for POST /event-form-answers/by-order (every buyer)
  formAnswersToken?: string;
}

// The AxiosError instance is kept as is, so a refused basket reaches the caller with the
// reasons the conflict carries (BasketErrorsConflict) still readable on its response.
const newTicketingOrder = (body: NewTicketingOrderBody) =>
  handleApiCall(() => privateAxios.post<NewTicketingOrderResponse>(`/ticketing`, body));

export function useNewTicketingOrderMutation() {
  return useMutation({
    mutationFn: newTicketingOrder,
  });
}

async function upsertExternalTicketing(
  eventId: string,
  body: UpsertExternalTicketingBody
) {
  try {
    const { data: response } =
      await privateAxios.put<UpsertExternalTicketingResponse>(
        `/ticketing/external/${eventId}`,
        body
      );

    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
}

async function deleteExternalTicketing(eventId: string) {
  try {
    const { data: response } = await privateAxios.delete<DeleteExternalTicketingResponse>(
      `/ticketing/external/${eventId}`
    );

    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
}

export function useUpsertExternalTicketingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      htmlContent,
    }: {
      eventId: string;
      htmlContent: string;
    }) => upsertExternalTicketing(eventId, { htmlContent }),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: ticketingQueryKeys.event(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
}

export function useDeleteExternalTicketingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId }: { eventId: string }) => deleteExternalTicketing(eventId),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: ticketingQueryKeys.event(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
}
